import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowRight, LogOut } from "lucide-react";
import { getSetupStatus } from "@/lib/api/employees.functions";
import { supabase } from "@/integrations/supabase/client";
import { ACTIVE_STAFF_KEY, ACTIVE_STAFF_NAME_KEY } from "@/lib/active-staff";

export const Route = createFileRoute("/_authenticated/bem-vindo")({
  component: WelcomePage,
});

function WelcomePage() {
  const navigate = useNavigate();
  const { role } = Route.useRouteContext() as { role?: string };
  const status = useServerFn(getSetupStatus);
  const [tcName, setTcName] = useState<string | null>(null);
  const [managerName, setManagerName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmSwitch, setConfirmSwitch] = useState(false);

  useEffect(() => {
    // Guarda: responsáveis não devem ver esta tela
    if (role === "parent_student") {
      navigate({ to: "/responsavel/alunos", replace: true });
      return;
    }
    (async () => {
      try {
        const s = await status();
        if (!s.hasTrainingCenter) {
          navigate({ to: "/m/$slug", params: { slug: "dados-ct" } });
          return;
        }
        setTcName(s.tcName);
        setManagerName((s as any).managerName ?? null);
      } catch (e: any) {
        const msg = String(e?.message || "");
        if (msg.includes("Unauthorized") || msg.includes("authorization")) {
          navigate({ to: "/auth", replace: true });
          return;
        }
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [role]);

  const onContinue = () => navigate({ to: "/selecionar-perfil" });

  const signOut = async () => {
    localStorage.removeItem(ACTIVE_STAFF_KEY);
    localStorage.removeItem(ACTIVE_STAFF_NAME_KEY);
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  const signOutAndReregister = async () => {
    localStorage.removeItem(ACTIVE_STAFF_KEY);
    localStorage.removeItem(ACTIVE_STAFF_NAME_KEY);
    await supabase.auth.signOut();
    window.location.href = "/auth";
  };

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center text-center px-4 relative">
      <div className="absolute top-2 right-4">
        <Button variant="ghost" size="sm" onClick={signOut}>
          <LogOut className="h-4 w-4 mr-2" /> Sair do Sistema
        </Button>
      </div>

      <h1 className="text-3xl sm:text-4xl font-bold mb-2">
        Bem-vindo(a){managerName ? `, ${managerName.split(" ")[0]}` : ""}!
      </h1>
      <p className="text-muted-foreground mb-8">
        Confira os detalhes do seu perfil abaixo antes de prosseguir.
      </p>

      <Card className="w-full max-w-xl mb-8">
        <div className="bg-primary/10 px-6 py-4 rounded-t-xl text-left">
          <h2 className="text-lg font-semibold">
            {loading ? "Carregando..." : tcName ?? "Centro de Treinamento"}
          </h2>
        </div>
        <CardContent className="p-6 text-left">
          <h3 className="font-semibold mb-1">Sobre o Centro de Treinamento</h3>
          <p className="text-sm text-muted-foreground">
            Acesse o sistema para gerenciar alunos, treinos, financeiro e muito mais.
          </p>
        </CardContent>
      </Card>

      <Button size="lg" onClick={onContinue} className="bg-primary hover:bg-primary/90">
        Acessar Sistema <ArrowRight className="ml-2 h-4 w-4" />
      </Button>

      <button
        type="button"
        onClick={() => setConfirmSwitch(true)}
        className="mt-6 text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
      >
        Cadastrou com o perfil errado? Alterar Perfil
      </button>

      <AlertDialog open={confirmSwitch} onOpenChange={setConfirmSwitch}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alterar perfil de acesso</AlertDialogTitle>
            <AlertDialogDescription>
              O perfil (Centro de Treinamento ou Usuário) é definido no momento do
              cadastro e não pode ser trocado depois. Para alterar, você precisa
              sair e criar uma nova conta com o perfil correto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={signOutAndReregister}>
              Sair e recadastrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
